import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiConfigurationService } from './Common/ApiConfigurationService/api-configuration.service';
import { ApiConfigurationDTO } from './Common/ApiConfigurationService/api-configuration.interface';

// Per-request opt-out, not a per-origin one: set via `{ context: new
// HttpContext().set(SKIP_TENANT_URL_REWRITE, true) }` on an individual
// request's options. Deliberately request-scoped rather than matching by
// origin/prefix (like identityRoutedPrefixes below) — the support chat
// widget (support-chat-widget.component.ts) needs its SPD calls exempted
// from tenant rewriting, but the admin's own SupportDesk pages hit the
// exact same SPD origin via TicketService/DiscussionService and must keep
// being rewritten normally. An origin-based exemption can't tell those two
// callers apart; only tagging the specific request can.
export const SKIP_TENANT_URL_REWRITE = new HttpContextToken<boolean>(() => false);

// Rewrites the origin of outgoing requests from the compiled-in
// environment.*BaseURL default to this tenant's ServerPool-resolved URL,
// leaving the path (and everything ValidationInterceptor does) untouched.
//
// Why an interceptor and not a GlobalVariables.ts rewrite: every one of the
// hundreds of endpoint constants in GlobalVariables.ts is a plain string
// built once at module-load time (e.g. `ERP + '/Billing/SalesMaster/Create'`
// where `ERP = environment.ERPBaseURL + '/ERP'`). Mutating environment.*BaseURL
// after that module has evaluated does not change those already-baked
// strings. Rewriting the origin per-request here is the only point that
// still has each full absolute URL, so it's the only point that can swap it.
@Injectable()
export class ApiUrlRewriteInterceptor implements HttpInterceptor {

  // origin (as compiled into environment.ts) -> ApiConfigurationDTO key.
  // Built once from the static environment object, not per-request.
  private readonly originToOverrideKey: Record<string, keyof ApiConfigurationDTO> = {
    [environment.AMBaseURL]: 'am',
    [environment.ERPBaseURL]: 'erp',
    [environment.CRMBaseURL]: 'crm',
    [environment.HRMSBaseURL]: 'hrms',
    [environment.SPDBaseURL]: 'spd',
    [environment.PMSBaseURL]: 'pms',
    [environment.BIRBaseURL]: 'bir',
    [environment.AIBaseURL]: 'ai',
    [environment.DOBaseURL]: 'do',
    [environment.NTBaseURL]: 'nt',
    [environment.PRBaseURL]: 'pr',
    [environment.EXTBaseURL]: 'ext',
  };

  // Endpoints hardcoded to always hit AdvinPROD01 (the server hosting the
  // [UseIdentityServer]/IDENTITYDBConn-routed identity endpoints) instead of
  // being built from environment.AMBaseURL. These must never be rewritten by
  // a cached tenant override below - that's precisely the bootstrap loop
  // (host -> tenantId -> gateway URLs) these two calls exist to break.
  private readonly identityRoutedPrefixes = [
    environment.ValueListAPI,
    environment.APIConfig,
  ];

  constructor(private apiConfigurationService: ApiConfigurationService) { }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Local dev (environment.ts, production: false) always uses the
    // compiled-in dev.apig.*.vnccy.com URLs as-is — never rewritten to a
    // tenant's ServerPool-resolved override.
    if (!environment.production) {
      return next.handle(request);
    }

    if (request.context.get(SKIP_TENANT_URL_REWRITE)) {
      return next.handle(request);
    }

    if (this.identityRoutedPrefixes.some(prefix => request.url.startsWith(prefix))) {
      return next.handle(request);
    }

    let origin: string;
    try {
      origin = new URL(request.url).origin;
    } catch {
      // Relative URL (or not a URL at all) — nothing to rewrite.
      return next.handle(request);
    }

    const overrideKey = this.originToOverrideKey[origin];
    if (!overrideKey) {
      // Not one of the module origins this interceptor knows how to rewrite
      // (e.g. a third-party call) — leave it alone.
      return next.handle(request);
    }

    const overrides = this.apiConfigurationService.getOverrides();
    if (!overrides) {
      // Tenant config was never loaded, or the GetApiConfiguration call
      // failed/timed out. Previously this silently sent the request to the
      // compiled-in default host — which, for a tenant on a dedicated/pool
      // server, is the WRONG server. That produced confusing 500s that look
      // like a backend bug but are actually a routing bug. Block instead of
      // guessing: fail loudly so the real cause (config never loaded) is
      // visible, rather than masking it behind a wrong-server 500.
      return throwError(() => new HttpErrorResponse({
        status: 0,
        statusText: 'Tenant configuration unavailable',
        url: request.url,
        error: { Message: 'Tenant gateway configuration is not loaded yet. Please retry.' }
      }));
    }

    // A present `overrides` object with this specific key absent/null is a
    // legitimate signal from GetApiConfiguration — that module genuinely
    // isn't overridden for this tenant (see ApiConfigurationDTO) — so falling
    // back to the compiled-in default below is correct, not a bug.
    const rawOverrideBaseUrl = overrides[overrideKey];

    // ServerPool's GetApiConfiguration always returns each URL with a trailing
    // slash (e.g. "https://apig.scpl.am.advinsoftwares.com/"), but
    // `new URL(...).origin` below never includes one. Without stripping it
    // here, the replace() a few lines down produces a double slash
    // ("...com//AM/...") that Ocelot fails to route (404/500) on every
    // request after the first successful GetApiConfiguration call.
    const overrideBaseUrl = rawOverrideBaseUrl ? rawOverrideBaseUrl.replace(/\/+$/, '') : undefined;

    if (!overrideBaseUrl || overrideBaseUrl === origin) {
      return next.handle(request);
    }

    console.log(`ApiUrlRewriteInterceptor: rewriting ${request.url} -> ${overrideBaseUrl}${request.url.substring(origin.length)}`);
    request = request.clone({ url: request.url.replace(origin, overrideBaseUrl) });
    return next.handle(request);
  }
}
